import React, { useEffect, useRef, useState } from 'react';
import { hasCompletedVideoWatch } from '../../utils/lessonSequence';

let youtubeApiPromise;

const loadYoutubeApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve, reject) => {
    const existingCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof existingCallback === 'function') existingCallback();
      resolve(window.YT);
    };

    if (!document.querySelector('script[data-shem-youtube-api]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.dataset.shemYoutubeApi = 'true';
      script.onerror = () => reject(new Error('The YouTube player could not be loaded.'));
      document.head.appendChild(script);
    }
  });

  return youtubeApiPromise;
};

export default function SequentialVideoPlayer({ videoId, title, onEnded }) {
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const onEndedRef = useRef(onEnded);
  const watchTimerRef = useRef(null);
  const lastTickRef = useRef(null);
  const watchedSecondsRef = useRef(0);
  const [status, setStatus] = useState('loading');
  const [watchMessage, setWatchMessage] = useState('');

  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setWatchMessage('');
    watchedSecondsRef.current = 0;

    const stopWatchTimer = () => {
      if (watchTimerRef.current) window.clearInterval(watchTimerRef.current);
      watchTimerRef.current = null;
      lastTickRef.current = null;
    };

    const startWatchTimer = (player) => {
      if (watchTimerRef.current) return;
      lastTickRef.current = window.performance.now();
      watchTimerRef.current = window.setInterval(() => {
        const now = window.performance.now();
        const elapsedSeconds = Math.max(0, (now - lastTickRef.current) / 1000);
        const playbackRate = Number(player.getPlaybackRate?.() || 1);
        watchedSecondsRef.current += elapsedSeconds * playbackRate;
        lastTickRef.current = now;
      }, 1000);
    };

    loadYoutubeApi()
      .then((YT) => {
        if (cancelled || !mountRef.current) return;
        playerRef.current = new YT.Player(mountRef.current, {
          host: 'https://www.youtube-nocookie.com',
          videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            controls: 1,
            fs: 1,
            iv_load_policy: 3,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
            origin: window.location.origin
          },
          events: {
            onReady: (event) => {
              if (cancelled) return;
              const iframe = event.target.getIframe();
              iframe.title = title;
              setStatus('ready');
            },
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                setWatchMessage('');
                startWatchTimer(event.target);
                return;
              }
              stopWatchTimer();
              if (event.data === YT.PlayerState.ENDED) {
                const duration = Number(event.target.getDuration?.() || 0);
                const watchedEnough = hasCompletedVideoWatch({
                  duration,
                  watchedSeconds: watchedSecondsRef.current
                });
                if (watchedEnough) {
                  onEndedRef.current?.();
                } else {
                  setWatchMessage('This lesson was not marked complete because most of the video was skipped. Play it through before continuing.');
                }
              }
            },
            onError: () => {
              if (!cancelled) setStatus('error');
            }
          }
        });
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      stopWatchTimer();
      try {
        playerRef.current?.destroy();
      } catch (error) {
        // The API may still be initializing when the modal closes.
      }
      playerRef.current = null;
    };
  }, [title, videoId]);

  return (
    <div className="mc-video-player" aria-busy={status === 'loading'}>
      {status === 'loading' && <div className="mc-video-player-status">Loading video…</div>}
      {status === 'error' && (
        <div className="mc-video-player-status is-error">
          This video could not be loaded. Check your connection and try again.
        </div>
      )}
      <div ref={mountRef} className={`mc-video-player-mount${status === 'error' ? ' is-hidden' : ''}`} />
      {watchMessage && <div className="mc-video-watch-message" role="status">{watchMessage}</div>}
    </div>
  );
}
